import { useId, type ReactNode } from 'react';

interface FieldProps {
  label: string;
  /** Optional help text rendered under the control and wired up via aria-describedby. */
  hint?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  /** Marks the control invalid and exposes the reason to assistive technology. */
  invalid?: boolean;
  placeholder?: string;
  inputMode?: 'text' | 'numeric';
  /** Stable hook for E2E selectors. */
  testId?: string;
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  invalid = false,
  placeholder,
  inputMode = 'text',
  testId,
}: FieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        inputMode={inputMode}
        value={value}
        placeholder={placeholder}
        aria-invalid={invalid}
        aria-describedby={hint ? hintId : undefined}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  hint?: ReactNode;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  testId?: string;
}

export function NumberField({
  label,
  hint,
  value,
  onChange,
  min,
  max,
  step,
  testId,
}: NumberFieldProps) {
  const id = useId();
  const hintId = `${id}-hint`;
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        value={Number.isFinite(value) ? value : ''}
        min={min}
        max={max}
        step={step}
        aria-describedby={hint ? hintId : undefined}
        data-testid={testId}
        onChange={(e) => {
          const next = Number(e.target.value);
          if (Number.isFinite(next)) onChange(next);
        }}
      />
      {hint ? (
        <span className="hint" id={hintId}>
          {hint}
        </span>
      ) : null}
    </div>
  );
}
